import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { sancionServiceFirestore } from '../../services/SancionServiceFirestore';
import { reclamacionServiceFirestore } from '../../services/ReclamacionServiceFirestore';
import { FaPlus, FaEdit, FaTrash, FaRobot, FaUserEdit, FaFilter, FaCarSide, FaUser, FaGavel } from '../../components/Icons';
import { FaExclamationTriangle, FaCheck, FaTimes, FaEye } from 'react-icons/fa';
import { theme } from '../../theme';

// Breakpoints
const breakpoints = {
  mobile: '768px',
  tablet: '1024px'
};

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 8px;
    margin: 0;
    max-width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  border-bottom: 2px solid ${theme.colors.primary.main};
  padding-bottom: 15px;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
    padding-bottom: 12px;
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    color: ${theme.colors.primary.main};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.3rem;
    text-align: center;
    justify-content: center;
  }
`;

const Button = styled.button`
  background-color: ${theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: ${theme.colors.secondary.main};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  svg {
    margin-right: 8px;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 100%;
    justify-content: center;
    padding: 12px 16px;
    font-size: 0.9rem;
    
    &:hover {
      transform: none;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  
  @media (max-width: ${breakpoints.mobile}) {
    display: none;
  }
`;

const Th = styled.th`
  background-color: ${theme.colors.primary.main};
  color: white;
  text-align: left;
  padding: 15px;
  font-weight: 600;
  position: relative;
  
  &:first-child {
    border-top-left-radius: 8px;
  }
  
  &:last-child {
    border-top-right-radius: 8px;
  }
`;

const Td = styled.td`
  padding: 15px;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s;
  vertical-align: middle;
  
  tr:last-child & {
    border-bottom: none;
  }
  
  tr:hover & {
    background-color: ${theme.colors.background.default};
  }
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin-right: 10px;
  color: ${theme.colors.primary.main};
  font-size: 1.1rem;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${theme.colors.secondary.main};
    background-color: rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  color: ${theme.colors.text.secondary};
  
  svg {
    font-size: 3rem;
    color: ${theme.colors.primary.main};
    opacity: 0.7;
  }
  
  span {
    font-size: 1.1rem;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
  }
`;

const FilterButton = styled.button<{ $active: boolean }>`
  background-color: ${props => props.$active ? theme.colors.primary.main : 'white'};
  color: ${props => props.$active ? 'white' : theme.colors.primary.main};
  border: 1px solid ${theme.colors.primary.main};
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  font-weight: 500;
  
  &:hover {
    background-color: ${props => props.$active ? theme.colors.primary.main : theme.colors.background.default};
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  svg {
    margin-right: 5px;
  }
`;

const Badge = styled.span<{ $type: 'auto' | 'manual' }>`
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  background-color: ${props => props.$type === 'auto' ? theme.colors.info.main : theme.colors.secondary.main};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  }
`;

const InfoMessage = styled.div`
  background-color: rgba(16, 19, 37, 0.03);
  border-left: 4px solid ${theme.colors.primary.main};
  color: ${theme.colors.text.primary};
  padding: 16px 20px;
  margin-bottom: 25px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const WarningMessage = styled.div`
  background-color: ${theme.colors.error.light};
  border-left: 4px solid ${theme.colors.error.main};
  color: ${theme.colors.error.dark};
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  line-height: 1.5;
`;

const ErrorMessage = styled.div`
  background-color: ${theme.colors.error.light};
  border-left: 4px solid ${theme.colors.error.main};
  color: ${theme.colors.error.dark};
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  line-height: 1.5;
`;

const SuccessMessage = styled.div`
  background-color: ${theme.colors.success.light};
  border-left: 4px solid ${theme.colors.success.main};
  color: ${theme.colors.success.dark};
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  line-height: 1.5;
`;

const Tabs = styled.div`
  display: flex;
  margin-bottom: 20px;
  border-bottom: 1px solid #ddd;
  
  @media (max-width: ${breakpoints.mobile}) {
    margin-bottom: 16px;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  background-color: ${props => props.$active ? theme.colors.primary.main : 'transparent'};
  color: ${props => props.$active ? 'white' : theme.colors.text.primary};
  border: none;
  border-radius: 4px 4px 0 0;
  cursor: pointer;
  font-weight: ${props => props.$active ? '600' : '400'};
  transition: all 0.3s ease;
  margin-right: 5px;
  
  &:hover {
    background-color: ${props => props.$active ? theme.colors.primary.main : theme.colors.background.default};
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    flex: 1;
    text-align: center;
    padding: 12px 16px;
    font-size: 0.9rem;
    margin-right: 0;
    
    &:first-child {
      border-radius: 4px 0 0 0;
    }
    
    &:last-child {
      border-radius: 0 4px 0 0;
    }
  }
`;

// Componentes para vista móvil
const MobileCardContainer = styled.div`
  display: none;
  
  @media (max-width: ${breakpoints.mobile}) {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
  }
`;

const MobileCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${theme.colors.primary.main};
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const MobileCardTitle = styled.h3`
  margin: 0;
  color: ${theme.colors.primary.main};
  font-size: 1.1rem;
  font-weight: 600;
  flex: 1;
  margin-right: 8px;
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: 8px;
`;

const MobileCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const MobileCardLabel = styled.span`
  font-weight: 500;
  color: ${theme.colors.text.secondary};
  font-size: 0.9rem;
`;

const MobileCardValue = styled.span`
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  margin-left: 8px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
  }
  
  select {
    flex: 1;
    padding: 10px;
    border-radius: 4px;
    border: 1px solid #ddd;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
    
    @media (max-width: ${breakpoints.mobile}) {
      width: 100%;
    }
  }
`;

const SancionesList: React.FC = () => {
  const navigate = useNavigate();
  const { data, loadData } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filtroTipo, setFiltroTipo] = useState<'todas' | 'automaticas' | 'manuales'>('todas');
  const [filtroCarrera, setFiltroCarrera] = useState<string>('todas');
  const [filtroPiloto, setFiltroPiloto] = useState<string>('todos');
  const [activeTab, setActiveTab] = useState<'sanciones' | 'reclamaciones'>('sanciones');
  const [isComisario, setIsComisario] = useState(true); // Por defecto, asumimos que es comisario en esta página
  
  // En una implementación real, verificaríamos el rol del usuario
  useEffect(() => {
    // Simular verificación de rol de comisario
    // En una implementación real, esto vendría del sistema de autenticación
    const checkComisarioRole = () => {
      // En esta página, asumimos que el usuario es comisario
      setIsComisario(true);
    };
    
    checkComisarioRole();
  }, []);
  
  // Eliminar una sanción
  const handleDeleteSancion = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta sanción?')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const deleted = await sancionServiceFirestore.delete(id);
      if (deleted) {
        setSuccess('Sanción eliminada correctamente');
        loadData();
      } else {
        setError('Error al eliminar la sanción');
      }
    } catch (err) {
      console.error('Error al eliminar sanción:', err);
      setError('Error al eliminar la sanción');
    } finally {
      setLoading(false);
    }
  };
  
  // Filtrar sanciones
  const getSancionesFiltradas = () => {
    let sanciones = [...data.sanciones];
    
    // Filtrar por tipo (automática o manual)
    if (filtroTipo === 'automaticas') {
      sanciones = sanciones.filter(sancion => sancion.automatica);
    } else if (filtroTipo === 'manuales') {
      sanciones = sanciones.filter(sancion => !sancion.automatica);
    }
    
    // Filtrar por carrera
    if (filtroCarrera !== 'todas') {
      sanciones = sanciones.filter(sancion => sancion.carreraId === filtroCarrera);
    }
    
    // Filtrar por piloto
    if (filtroPiloto !== 'todos') {
      sanciones = sanciones.filter(sancion => sancion.socioId === filtroPiloto);
    }
    
    return sanciones;
  };
  
  // Obtener nombre de la carrera
  const getNombreCarrera = (carreraId: string) => {
    const carrera = data.carreras.find(c => c.id === carreraId);
    return carrera ? carrera.nombre : 'General';
  };
  
  // Obtener nombre del piloto
  const getNombrePiloto = (socioId: string) => {
    const socio = data.socios.find(s => s.id === socioId);
    if (!socio) return 'Desconocido';
    
    // Usar display_name si está disponible, de lo contrario usar nombre y apellido
    return socio.display_name || (socio.nombre && socio.apellido ? `${socio.nombre} ${socio.apellido}` : socio.nombre || 'Sin nombre');
  };
  
  // Obtener carreras únicas para el filtro
  const getCarrerasUnicas = () => {
    const carrerasIds = new Set<string>();
    data.sanciones.forEach(sancion => {
      if (sancion.carreraId) {
        carrerasIds.add(sancion.carreraId);
      }
    });
    
    return Array.from(carrerasIds).map(id => {
      const carrera = data.carreras.find(c => c.id === id);
      return {
        id,
        nombre: carrera ? carrera.nombre : 'Desconocido'
      };
    });
  };
  
  // Obtener pilotos únicos para el filtro
  const getPilotosUnicos = () => {
    const pilotosIds = new Set<string>();
    data.sanciones.forEach(sancion => {
      pilotosIds.add(sancion.socioId);
    });
    
    return Array.from(pilotosIds).map(id => {
      const socio = data.socios.find(s => s.id === id);
      return {
        id,
        nombre: socio ? `${socio.nombre} ${socio.apellido}` : 'Desconocido'
      };
    });
  };
  
  // Obtener reclamaciones pendientes
  const getReclamacionesPendientes = () => {
    return data.reclamaciones.filter(reclamacion => reclamacion.estado === 'pendiente');
  };
  
  // Obtener nombre de liga por ID
  const getNombreLiga = (ligaId: string) => {
    const liga = data.ligas.find(l => l.id === ligaId);
    return liga ? liga.nombre : 'Liga desconocida';
  };
  
  const sancionesFiltradas = getSancionesFiltradas();
  
  return (
    <Container>
      <Header>
        <Title><FaGavel /> {activeTab === 'sanciones' ? 'Sistema de Sanciones' : 'Reclamaciones Pendientes'}</Title>
        <Button as={Link} to="/sanciones/new">
          <FaPlus /> Nueva Sanción
        </Button>
      </Header>
      
      {/* Tabs para alternar entre sanciones y reclamaciones pendientes */}
      <Tabs>
        <Tab 
          $active={activeTab === 'sanciones'} 
          onClick={() => setActiveTab('sanciones')}
        >
          Sanciones
        </Tab>
        <Tab 
          $active={activeTab === 'reclamaciones'} 
          onClick={() => setActiveTab('reclamaciones')}
        >
          Reclamaciones Pendientes
        </Tab>
      </Tabs>
      
      <InfoMessage>
        El sistema de comisarios permite gestionar sanciones para los pilotos, ya sea de forma automática o manual. Las sanciones pueden estar asociadas a una carrera específica o ser generales.
      </InfoMessage>
      
      {error && (
        <WarningMessage>
          {error}
        </WarningMessage>
      )}
      
      {success && (
        <InfoMessage style={{ backgroundColor: '#d4edda', borderLeftColor: '#28a745', color: '#155724' }}>
          {success}
        </InfoMessage>
      )}
      
      {activeTab === 'sanciones' ? (
        <>
          {/* Filtros para sanciones */}
          <FilterContainer>
            <FilterButton 
              $active={filtroTipo === 'todas'} 
              onClick={() => setFiltroTipo('todas')}
            >
              <FaFilter /> Todas
            </FilterButton>
            <FilterButton 
              $active={filtroTipo === 'automaticas'} 
              onClick={() => setFiltroTipo('automaticas')}
            >
              <FaRobot /> Automáticas
            </FilterButton>
            <FilterButton 
              $active={filtroTipo === 'manuales'} 
              onClick={() => setFiltroTipo('manuales')}
            >
              <FaUserEdit /> Manuales
            </FilterButton>
          </FilterContainer>
          
          <FilterContainer>
            <FilterRow>
              <select 
                value={filtroCarrera} 
                onChange={(e) => setFiltroCarrera(e.target.value)}
              >
                <option value="todas">Todas las carreras</option>
                {getCarrerasUnicas().map(carrera => (
                  <option key={carrera.id} value={carrera.id}>{carrera.nombre}</option>
                ))}
              </select>
              
              <select 
                value={filtroPiloto} 
                onChange={(e) => setFiltroPiloto(e.target.value)}
              >
                <option value="todos">Todos los pilotos</option>
                {getPilotosUnicos().map(piloto => (
                  <option key={piloto.id} value={piloto.id}>{piloto.nombre}</option>
                ))}
              </select>
            </FilterRow>
          </FilterContainer>
          
          {/* Tabla de sanciones */}
          {sancionesFiltradas.length > 0 ? (
            <>
            <Table>
              <thead>
                <tr>
                  <Th>Nombre</Th>
                  <Th>Piloto</Th>
                  <Th>Carrera</Th>
                  <Th>Puntos</Th>
                  <Th>Fecha</Th>
                  <Th>Tipo</Th>
                  <Th>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {sancionesFiltradas.map(sancion => (
                  <tr key={sancion.id}>
                    <Td>{sancion.nombre}</Td>
                    <Td>{getNombrePiloto(sancion.socioId)}</Td>
                    <Td>{getNombreCarrera(sancion.carreraId)}</Td>
                    <Td style={{ color: sancion.puntosPenalizacion < 0 ? '#dc3545' : '#28a745' }}>
                      {sancion.puntosPenalizacion > 0 ? `+${sancion.puntosPenalizacion}` : sancion.puntosPenalizacion}
                    </Td>
                    <Td>{sancion.fechaAplicacion}</Td>
                    <Td>
                      {sancion.automatica ? 
                        <Badge $type="auto"><FaRobot style={{ marginRight: '3px' }} /> Auto</Badge> : 
                        <Badge $type="manual"><FaUserEdit style={{ marginRight: '3px' }} /> Manual</Badge>
                      }
                    </Td>
                    <Td>
                      <Link to={`/sanciones/${sancion.id}`}>
                        <ActionButton title="Editar sanción">
                          <FaEdit />
                        </ActionButton>
                      </Link>
                      <ActionButton 
                        onClick={() => handleDeleteSancion(sancion.id)}
                        title="Eliminar sanción"
                        disabled={loading}
                      >
                        <FaTrash />
                      </ActionButton>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {/* Vista móvil - Tarjetas */}
            <MobileCardContainer>
              {sancionesFiltradas.map(sancion => (
                <MobileCard key={sancion.id}>
                  <MobileCardHeader>
                    <MobileCardTitle>{sancion.nombre}</MobileCardTitle>
                    <MobileCardActions>
                      <Link to={`/sanciones/${sancion.id}`}>
                        <ActionButton title="Editar sanción">
                          <FaEdit />
                        </ActionButton>
                      </Link>
                      <ActionButton 
                        onClick={() => handleDeleteSancion(sancion.id)}
                        title="Eliminar sanción"
                        disabled={loading}
                      >
                        <FaTrash />
                      </ActionButton>
                    </MobileCardActions>
                  </MobileCardHeader>
                  
                  <MobileCardContent>
                    <MobileCardRow>
                      <MobileCardLabel>Piloto:</MobileCardLabel>
                      <MobileCardValue>{getNombrePiloto(sancion.socioId)}</MobileCardValue>
                    </MobileCardRow>
                    
                    <MobileCardRow>
                      <MobileCardLabel>Carrera:</MobileCardLabel>
                      <MobileCardValue>{getNombreCarrera(sancion.carreraId)}</MobileCardValue>
                    </MobileCardRow>
                    
                    <MobileCardRow>
                      <MobileCardLabel>Puntos:</MobileCardLabel>
                      <MobileCardValue style={{ color: sancion.puntosPenalizacion < 0 ? '#dc3545' : '#28a745' }}>
                        {sancion.puntosPenalizacion > 0 ? `+${sancion.puntosPenalizacion}` : sancion.puntosPenalizacion}
                      </MobileCardValue>
                    </MobileCardRow>
                    
                    <MobileCardRow>
                      <MobileCardLabel>Fecha:</MobileCardLabel>
                      <MobileCardValue>{sancion.fechaAplicacion}</MobileCardValue>
                    </MobileCardRow>
                    
                    <MobileCardRow>
                      <MobileCardLabel>Tipo:</MobileCardLabel>
                      <MobileCardValue>
                        {sancion.automatica ? 
                          <Badge $type="auto"><FaRobot style={{ marginRight: '3px' }} /> Auto</Badge> : 
                          <Badge $type="manual"><FaUserEdit style={{ marginRight: '3px' }} /> Manual</Badge>
                        }
                      </MobileCardValue>
                    </MobileCardRow>
                  </MobileCardContent>
                </MobileCard>
              ))}
            </MobileCardContainer>
            </>
          ) : (
            <EmptyMessage>
              <FaGavel />
              <span>No hay sanciones registradas que coincidan con los filtros aplicados. Crea una nueva sanción para comenzar.</span>
            </EmptyMessage>
          )}
        </>
      ) : (
        // Tabla de reclamaciones pendientes
        getReclamacionesPendientes().length > 0 ? (
          <>
          <Table>
            <thead>
              <tr>
                <Th>Fecha</Th>
                <Th>Liga</Th>
                <Th>Carrera</Th>
                <Th>Reclamante</Th>
                <Th>Reclamado</Th>
                <Th>Motivo</Th>
                <Th>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {getReclamacionesPendientes().map(reclamacion => (
                <tr key={reclamacion.id}>
                  <Td>{reclamacion.fechaReclamacion}</Td>
                  <Td>{getNombreLiga(reclamacion.ligaId)}</Td>
                  <Td>{getNombreCarrera(reclamacion.carreraId)}</Td>
                  <Td>{getNombrePiloto(reclamacion.reclamanteSocioId)}</Td>
                  <Td>{getNombrePiloto(reclamacion.reclamadoSocioId)}</Td>
                  <Td>{reclamacion.motivo}</Td>
                  <Td>
                    <ActionButton onClick={() => navigate(`/reclamaciones/${reclamacion.id}`)}>
                      <FaEye size={16} title="Ver detalles" />
                    </ActionButton>
                    <ActionButton onClick={() => navigate(`/sanciones/new?reclamacion=${reclamacion.id}`)}>
                      <FaGavel size={16} title="Aplicar sanción" />
                    </ActionButton>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {/* Vista móvil - Tarjetas para reclamaciones */}
          <MobileCardContainer>
            {getReclamacionesPendientes().map(reclamacion => (
              <MobileCard key={reclamacion.id}>
                <MobileCardHeader>
                  <MobileCardTitle>Reclamación</MobileCardTitle>
                  <MobileCardActions>
                    <ActionButton onClick={() => navigate(`/reclamaciones/${reclamacion.id}`)}>
                      <FaEye title="Ver detalles" />
                    </ActionButton>
                    <ActionButton onClick={() => navigate(`/sanciones/new?reclamacion=${reclamacion.id}`)}>
                      <FaGavel title="Aplicar sanción" />
                    </ActionButton>
                  </MobileCardActions>
                </MobileCardHeader>
                
                <MobileCardContent>
                  <MobileCardRow>
                    <MobileCardLabel>Fecha:</MobileCardLabel>
                    <MobileCardValue>{reclamacion.fechaReclamacion}</MobileCardValue>
                  </MobileCardRow>
                  
                  <MobileCardRow>
                    <MobileCardLabel>Liga:</MobileCardLabel>
                    <MobileCardValue>{getNombreLiga(reclamacion.ligaId)}</MobileCardValue>
                  </MobileCardRow>
                  
                  <MobileCardRow>
                    <MobileCardLabel>Carrera:</MobileCardLabel>
                    <MobileCardValue>{getNombreCarrera(reclamacion.carreraId)}</MobileCardValue>
                  </MobileCardRow>
                  
                  <MobileCardRow>
                    <MobileCardLabel>Reclamante:</MobileCardLabel>
                    <MobileCardValue>{getNombrePiloto(reclamacion.reclamanteSocioId)}</MobileCardValue>
                  </MobileCardRow>
                  
                  <MobileCardRow>
                    <MobileCardLabel>Reclamado:</MobileCardLabel>
                    <MobileCardValue>{getNombrePiloto(reclamacion.reclamadoSocioId)}</MobileCardValue>
                  </MobileCardRow>
                  
                  <MobileCardRow>
                    <MobileCardLabel>Motivo:</MobileCardLabel>
                    <MobileCardValue>{reclamacion.motivo}</MobileCardValue>
                  </MobileCardRow>
                </MobileCardContent>
              </MobileCard>
                         ))}
           </MobileCardContainer>
           </>
        ) : (
          <EmptyMessage>
            <FaExclamationTriangle size={40} />
            <span>No hay reclamaciones pendientes. Todas las reclamaciones han sido procesadas.</span>
          </EmptyMessage>
        )
      )}
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
    </Container>
  );
};

export default SancionesList;
